import { useEffect, useState, useCallback } from 'react';
import { Button, Text, Alert} from '@smarterservices/smarter-ui';
import { checkExtension, uninstallExtension } from '../../helpers/helpers';
import Loader from '../Common/Loader';
import { useAppStore } from '../../stores/app';
/**
 * UninstallButton - A component that provides functionality to uninstall the browser extension
 * 
 * This component handles the complete lifecycle of extension uninstallation:
 * 1. Checking if the extension is installed
 * 2. Displaying an uninstall button if the extension is installed
 * 3. Showing a loading state during uninstallation
 * 4. Displaying a success message after uninstallation
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.buttonText - Custom text for the uninstall button
 * @param {string} props.uninstallingText - Text to display during uninstallation
 * @param {string} props.uninstalledText - Text to display after successful uninstallation
 * @returns {React.ReactElement} The rendered uninstall button component
 */
const UninstallButton = ({ buttonText, uninstallingText, uninstalledText }) => {
  const { apiClient } = useAppStore();
  /** @type {[boolean, Function]} Tracks if the extension is installed */
  const [extensionInstalled, setExtensionInstalled] = useState(false);
  
  /** @type {[boolean, Function]} Tracks if uninstallation is in progress */
  const [isUninstalling, setIsUninstalling] = useState(false);
  
  /** @type {[boolean, Function]} Tracks if uninstallation is complete */
  const [extensionUninstalled, setExtensionUninstalled] = useState(false);
  
  
  /**
   * Initializes extension data by checking if the extension is installed
   * 
   * @async
   * @function initExtensionData
   * @returns {Promise<void>}
   */
  const initExtensionData = useCallback(async () => {
    try {
      // Check if the extension is installed
      const isExtensionInstalled = await checkExtension();
      setExtensionInstalled(isExtensionInstalled);
      
    } catch (err) {
      console.error('Failed to check if the extension was installed', err);
    } 
  }, []);

  /**
   * Effect hook to check extension installation status when component mounts
   */
  useEffect(() => {
    initExtensionData();
    
    // Cleanup function in case component unmounts during data fetch
    return () => {
      // Any cleanup logic if needed when component unmounts
    };
  }, [initExtensionData]);

  /**
   * Handles the uninstallation process when the user clicks the uninstall button
   * 
   * This function:
   * 1. Sets the uninstalling state to show loading indicator
   * 2. Creates an annotation for the uninstallation event (currently commented out)
   * 3. Calls the uninstallExtension helper function
   * 4. Updates component state to reflect completion
   * 
   * @async
   * @function handleUninstall
   * @returns {Promise<void>}
   */
  const handleUninstall = async () => {
    // Show loading state
    setIsUninstalling(true);

    await apiClient.addAnnotation({
      payload: {
        typeId: 'extension-uninstalled',
      }

    });
    // Trigger extension uninstallation
    const wasUninstalled = await uninstallExtension();

    // Update component state
    if(wasUninstalled){
      setExtensionUninstalled(true);
    }
    setIsUninstalling(false);
    
  };

  /**
   * Renders the appropriate UI based on the current state:
   * - Uninstall button when extension is installed but not being uninstalled
   * - Loading indicator during uninstallation
   * - Success message after uninstallation completes
   */
  return (
    <>
        {/* Show uninstall button when extension is installed and not being uninstalled */}
        {(extensionInstalled && !isUninstalling && !extensionUninstalled) && (
            <Button
                color='primary'
                onClick={handleUninstall}
                data-testid="uninstall-extension-button"
            >
                {buttonText || 'Uninstall Extension'}
            </Button>
        )}
        {/* Show loading indicator during uninstallation */}
        {(extensionInstalled && isUninstalling && !extensionUninstalled) && (
            <Loader 
                textAs="span" 
                spinnerSize='x-small' 
                text="Uninstalling Extension" 
                textSize='medium'
                data-testid="uninstall-extension-loader"
            >
                {uninstallingText}
            </Loader>
        )}
        {/* Show success message after uninstallation */}
        {(extensionInstalled && !isUninstalling && extensionUninstalled) && (
            <Alert 
                variant='success'
                data-testid="uninstall-extension-success"
            >
                {uninstalledText}
            </Alert>
        )}
    </>
  );
};

export default UninstallButton;
